import classNames from 'classnames';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { usePlannerTranslations } from '../Planner/Context/PlannerTranslationsContextProvider';
import { SubjectList } from '../SubjectList/SubjectList';
import { useSummaryFocusManagerEvents } from '../SummaryFocusManager/useSummaryFocusManagerEvents';
import { AlertSectionVariant } from '~/Blocks/AlertSectionBlock/AlertSectionVariant.csharp';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { usePlannerContext } from '~/Features/StudyPage/Online/Components/Planner/Context/PlannerContextProvider';
import { IsSubjectPurchasable } from '~/Features/StudyPage/Online/Components/Utils/TypeGuards/PurchasableRestrictedSingleSubjectTypeGuard';
import type {
    PurchasableRestrictedSingleSubject,
    RestrictedSingleSubject,
} from '~/Features/StudyPage/Online/Interfaces/RestrictedSingleSubject';
import { CollapsibleAlertSection } from '~/Partials/AlertSection/CollapsibleAlertSection';
import { Collapse } from '~/Partials/Collapse/Collapse';
import { ModalWrapper } from '~/Partials/ModalWrapper/ModalWrapper';
import { animationDurationMs } from '~/Utils/AnimationsUtils';
import { useWindowWidthDetection } from '~/Utils/Hooks/useWindowWidthDetection';
import { MediaBreakpoints } from '~/Utils/MediaBreakpoints';
import type { Theme } from '~/Utils/Theme';
import styles from './Summary.module.css';
import type { SummaryFooterAction } from './SummaryFooter/SummaryFooter';
import { SummaryFooter } from './SummaryFooter/SummaryFooter';
import type { SummaryHeaderProps } from './SummaryHeader/SummaryHeader';
import { SummaryHeader } from './SummaryHeader/SummaryHeader';

export interface SummaryAlertProps {
    heading: string;
    description: string;
}

export interface SummaryProps extends Pick<SummaryHeaderProps, 'heading'> {
    isExpanded: boolean;
    handleClose: () => void;
    onSubjectActionClick: (subject: PurchasableRestrictedSingleSubject) => void;
    footerAction: SummaryFooterAction;
    subjects: PurchasableRestrictedSingleSubject[];
    warning?: SummaryAlertProps;
    theme?: Pick<Theme, 'withRoundedCorners'>;
    className?: string;
}

// We need this hook because we need to apply focus trap after content is fully rendered and contains a focusable element
const useExpandAnimationDetection = (isExpanded: boolean) => {
    const [isExpandAnimationCompleted, setIsExpandAnimationCompleted] = React.useState(false);

    const handleCollapseAnimation = () => {
        setIsExpandAnimationCompleted(isExpanded);
    };

    return { isExpandAnimationCompleted, handleCollapseAnimation };
};

export const Summary: React.FC<SummaryProps> = props => {
    const contextualTheme = useTheme();
    const {
        isExpanded,
        handleClose,
        onSubjectActionClick,
        footerAction,
        subjects,
        heading,
        warning,
        className,
        theme = contextualTheme,
    } = props;

    const { description, actions } = usePlannerTranslations();
    const { setError } = usePlannerContext();
    const { addSubjectsToProceed } = description;
    const { hideContent } = actions;

    const { isExpandAnimationCompleted, handleCollapseAnimation } = useExpandAnimationDetection(isExpanded);
    const isBelowDesktop = useWindowWidthDetection({ condition: 'lessThan', breakpoint: MediaBreakpoints.Desktop });
    const headingRef = useSummaryFocusManagerEvents<HTMLHeadingElement>();

    const backgroundRef = React.useRef<HTMLDivElement>(null);

    const { withRoundedCorners } = theme;

    const isActingLikeModal = isExpanded && !!isBelowDesktop;
    const hasSubjects = subjects.length !== 0;

    const renderSubjects = () => {
        if (!hasSubjects) {
            return <span className={styles.SubjectsFallback}>{addSubjectsToProceed}</span>;
        }

        const handleSubjectActionClick = (subject: RestrictedSingleSubject) =>
            IsSubjectPurchasable(subject)
                ? onSubjectActionClick(subject)
                : setError('removingNotPurchasableSubjectAttempt');

        return (
            <SubjectList
                isSubjectSelected={() => true}
                onSubjectActionClick={handleSubjectActionClick}
                subjects={subjects}
                subjectActionDisplayType="icon"
                subjectDisplayType="bare"
                size="compact"
            />
        );
    };

    const renderAlert = () => {
        if (warning) {
            return (
                <CollapsibleAlertSection
                    headerType="h4"
                    theme={{ withRoundedCorners: false }}
                    translations={{ close: hideContent }}
                    variant={AlertSectionVariant.Warning}
                    header={warning.heading}
                >
                    {warning.description}
                </CollapsibleAlertSection>
            );
        }
    };

    return (
        <div className={classNames(styles.Summary, withRoundedCorners && styles['Variant--rounded'], className)}>
            <h4 ref={headingRef} tabIndex={-1} className={styles.A11yHeader}>
                {heading}
            </h4>
            <CSSTransition
                nodeRef={backgroundRef}
                in={isActingLikeModal}
                timeout={animationDurationMs}
                appear
                unmountOnExit
            >
                <div ref={backgroundRef} className={styles.Background} />
            </CSSTransition>
            <ModalWrapper
                close={handleClose}
                isFocusTrapActive={isActingLikeModal && isExpandAnimationCompleted}
                isScrollLocked={isActingLikeModal}
            >
                {buttonClickClose => (
                    <div className={styles.Content}>
                        <div className={styles.CollapseWrapper}>
                            <Collapse isOpen={isExpanded} onRest={handleCollapseAnimation} className={styles.Collapse}>
                                <div className={styles.Inner}>
                                    <SummaryHeader heading={heading} onCollapseClick={buttonClickClose} />
                                    <div className={styles.Subjects}>{renderSubjects()}</div>
                                </div>
                                {renderAlert()}
                            </Collapse>
                        </div>
                        <Collapse isOpen={isExpanded || hasSubjects} className={styles.Collapse}>
                            <SummaryFooter action={footerAction} />
                        </Collapse>
                    </div>
                )}
            </ModalWrapper>
        </div>
    );
};
